import { HttpHeaders } from '@angular/common/http';
import type { AfterViewInit, OnDestroy, OnInit } from '@angular/core';
import { ChangeDetectorRef, Component, HostListener, inject } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { FeatureFlag } from 'app/common/enums/feature-flag.enum';
import moment from 'moment-timezone';

import { StringConstants } from 'app/rcr-exchange/constants/string-constants';
import { FeeDisclaimerService } from 'app/shared/services/fee-disclaimer.service';
import { FmLoadingPageService } from '../../shared/services/fm-loading-page.service';
import { SessionService } from '../../shared/services/session.service';
import { UserService } from '../../shared/services/user.service';
import type { MegaPricingSummary } from '../services/interfaces/MegaPricingSummary';
import { MegasService } from '../services/megas.service';
import { PassDataService } from '../services/pass-data.service';

@Component({
  standalone: false,
  selector: 'invd-confirmation-page',
  templateUrl: './confirmation-page.component.html',
  styleUrls: ['./confirmation-page.component.scss'],
})
export class ConfirmationPageComponent implements OnInit, AfterViewInit, OnDestroy {
  public commitResponse: any;
  public isAllowToNavigateAway = true;
  public feeTicks: string;
  public fee: string;
  public poolNumber: string;
  public downloadPDFResponse: any;
  public isEditMegaFlow = false;
  public req: any;
  public megaPricingSummary: MegaPricingSummary[];
  pricingSummary: any;
  pricingSummaryList: any;
  public isMayPricingMonth = false;
  totalMonthlyVolume: number;
  public showSubmitPoolInfo = true;
  public featuretoggle: string[] = [];
  public showEarlyCloseLegend = false;
  public showEarlyCloseLegendPricingSummary = false;
  public downloadPDFButtonClicked = false;
  public exportMultiMegaExcelLinkClicked = true;
  public disclaimerMessage = '';
  public recentlyCommittedDisclaimerMessage = '';

  private passDataService = inject(PassDataService);
  private megaService = inject(MegasService);
  public fmLoadingPageService = inject(FmLoadingPageService);
  private router = inject(Router);
  private userService = inject(UserService);
  private feeDisclaimerService = inject(FeeDisclaimerService);
  private sessionService = inject(SessionService);
  private cdr = inject(ChangeDetectorRef);

  @HostListener('window:beforeunload', ['$event'])
  refreshBtnClick($event) {
    $event.returnValue = true;
  }

  ngOnInit() {
    this.fmLoadingPageService.stopLoading();
    this.featuretoggle = this.userService.toggleFeatures;
    this.exportMultiMegaExcelLinkClicked = true;
    if (this.passDataService.getCommitMegaResponse() != null) {
      this.commitResponse = this.passDataService.getCommitMegaResponse();
      this.pricingSummary = this.commitResponse && this.commitResponse.megaPricingSummary[0];
      this.showEarlyCloseLegend = this.commitResponse && this.commitResponse.earlyCloseFlag;
      this.pricingSummaryList =
        this.commitResponse && this.commitResponse.megaPricingSummary[0].megaList;
      const pricingMonth = this.pricingSummary && this.pricingSummary.pricingMonth.split('-')[1];
      this.totalMonthlyVolume = this.pricingSummary && this.pricingSummary.monthlyVolume;
      if (pricingMonth === '05') {
        this.isMayPricingMonth = true;
      }
    }
    this.isEditMegaFlow = this.passDataService.getEditMegaFlow();
    this.poolNumber = this.commitResponse.poolNumber;
    this.showSubmitPoolInfo =
      this.userService.roles && this.userService.roles.includes('FJ5_PoolInfo_Provider')
        ? true
        : false;
    this.lookUpForEarlyCloseFlag();
  }

  ngAfterViewInit() {
    this.navigationController();
  }

  ngOnDestroy() {
    this.passDataService.setCommitMegaResponse(null);
    this.passDataService.setMegaRepopulateBundle(null);
    this.passDataService.setEditMegaFlow(false);
  }

  canDeactivate(): Promise<boolean> | boolean {
    return this.isAllowToNavigateAway;
  }

  public navigatePageTo(pageURL) {
    this.passDataService.setEditMegaFlow(false);
    this.router.navigate([pageURL]);
  }

  private navigationController() {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        if (event.url.indexOf('review-and-commit') > 0) {
          this.isAllowToNavigateAway = false;
        } else {
          this.isAllowToNavigateAway = true;
        }
      }
      if (!(event instanceof NavigationEnd)) {
        return;
      }
    });
  }

  public printWindow() {
    window.print();
  }

  public downloadMegaPDF(): void {
    this.fmLoadingPageService.isLoaderRequesting.set(true);
    this.downloadPDFButtonClicked = true;
    this.req = {
      poolNumber: this.commitResponse.poolNumber,
      isCollateralAnalyzer: false,
    };
    this.megaService.downloadMegaPDF(this.req).subscribe({
      next: (data) => this.handleDownloadPDFServiceSuccess(data),
      error: (error) => this.handleAWSDownloadPDFServiceFail(error),
    });
  }

  private handleDownloadPDFServiceSuccess(downloadPDFRes) {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.downloadPDFResponse = downloadPDFRes;
    const downloadedTime = moment(new Date());

    let data = this.downloadPDFResponse;
    const fileName =
      'Pool-' +
      this.commitResponse.poolNumber +
      '_Confirmation_' +
      downloadedTime.format('YYYYMMDD_HHmmss') +
      '.pdf';
    data = data.replace(/^[^,]+,/, '');
    data = data.replace(/\s/g, '');
    const byteCharacters = window.atob(data);
    const byteNumbers = Array.from({ length: byteCharacters.length });
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers as any);
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    if ((window.navigator as any).msSaveOrOpenBlob) {
      (window.navigator as any).msSaveOrOpenBlob(blob, fileName);
    } else {
      const objectUrl: string = URL.createObjectURL(blob);
      const a: HTMLAnchorElement = document.createElement('a') as HTMLAnchorElement;

      a.href = objectUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();

      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    }

    this.downloadPDFButtonClicked = false;
    this.cdr.markForCheck();
  }

  private handleAWSDownloadPDFServiceFail(_error: any): void {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.downloadPDFButtonClicked = false;
    this.cdr.markForCheck();
  }

  navigatePageToCommittedPools(): void {
    this.router.navigate(['/mega-trade/committed-mega']);
  }

  allowUploadCollateral(): void {
    let path = '';
    path =
      this.featuretoggle && this.featuretoggle.includes(FeatureFlag.COLAT_UPD_MENU)
        ? 'mega-upload/upload-pool-info'
        : 'mega-trade/committed-mega';
    this.router.navigate([path]);
  }

  exportMultiMegaExcelConfirmationDetails(): void {
    const exportConfirmationPageReq: any = [];
    this.fmLoadingPageService.isLoaderRequesting.set(true);
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'Content-Type': 'application/json',
        'x-fnma-sessionid': this.sessionService.sessionId,
        'x-fnma-channel': 'web',
        'x-fnma-sub-channel': this.sessionService.subChannel,
      }),
    };
    exportConfirmationPageReq.push(this.commitResponse.poolNumber);
    if (this.exportMultiMegaExcelLinkClicked) {
      this.megaService
        .exportMultiMegaExcelConfirmationDetails(exportConfirmationPageReq, httpOptions)
        .subscribe({
          next: (data) => this.handleEXCELExportConfirmationPageServiceSuccess(data),
          error: (error) => this.handleAWSExportConfirmationPageServiceFail(error),
        });
    }

    this.exportMultiMegaExcelLinkClicked = false;
  }

  private handleEXCELExportConfirmationPageServiceSuccess(res) {
    let fileName = '';
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    const downloadedTime = moment(new Date());
    let data = res;
    fileName = 'Pool_Confirmation_' + downloadedTime.format('YYYYMMDDHHmmss') + '.xlsx';
    data = data.replace(/^[^,]+,/, '');
    data = data.replace(/\s/g, '');
    const byteCharacters = window.atob(data);
    const byteNumbers = Array.from({ length: byteCharacters.length });
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers as any);
    const blob = new Blob([byteArray], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    if ((window.navigator as any).msSaveOrOpenBlob) {
      (window.navigator as any).msSaveOrOpenBlob(blob, fileName);
    } else {
      const objectUrl: string = URL.createObjectURL(blob);
      const a: HTMLAnchorElement = document.createElement('a') as HTMLAnchorElement;
      a.href = objectUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    }

    this.exportMultiMegaExcelLinkClicked = true;
    this.cdr.markForCheck();
  }

  private handleAWSExportConfirmationPageServiceFail(_error: any): void {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.exportMultiMegaExcelLinkClicked = true;
    this.cdr.markForCheck();
  }

  public lookUpForEarlyCloseFlag() {
    this.pricingSummaryList.forEach((item) => {
      if (item.earlyCloseFlag) {
        this.showEarlyCloseLegendPricingSummary = true;
      }
    });
  }

  public needsDisclaimer() {
    this.disclaimerMessage = StringConstants.SUPERS_PREFIX_FEE_DISCLAIMER;
  }

  public listNeedsDisclaimer() {
    this.recentlyCommittedDisclaimerMessage = StringConstants.SUPERS_PREFIX_FEE_DISCLAIMER;
  }

  public checkDisclaimerStatus(prefixCode: string) {
    return this.feeDisclaimerService.needsDisclaimer(prefixCode);
  }

  protected trackByIdentity(_index: number, obj: any) {
    return obj;
  }
}
